import { LANGUAGE_PICKER_OPTIONS } from '../../../../../generated/language-catalog';
// Native labels and regions come from the generated, deduplicated locale
// catalog. The product supplies the English names and their stable order.
const languageOption = (code, englishLabel) => {
    const nativeOption = LANGUAGE_PICKER_OPTIONS.find((option) => option.code === code);
    if (!nativeOption) {
        throw new Error(`Language picker option ${code} has no locale resource`);
    }
    const [firstCharacter, ...remainingCharacters] = Array.from(nativeOption.label);
    return Object.assign(Object.assign({}, nativeOption), { label: `${firstCharacter.toLocaleUpperCase(code)}${remainingCharacters.join('')}`, englishLabel });
};
const SIMPLIFIED_CHINESE_OPTION = languageOption('zh-CN', 'Chinese (Simplified, China)');
const TRADITIONAL_CHINESE_OPTION = languageOption('zh-TW', 'Chinese (Traditional, Taiwan)');
const FALLBACK_OPTION = languageOption('en', 'English (United States)');
// Product order: English language name, then the visible variant qualifier.
// Keep this sequence explicit so UI locale and browser collation cannot reorder it.
export const LANGUAGE_OPTIONS = [
    languageOption('ar', 'Arabic'),
    languageOption('bg', 'Bulgarian'),
    languageOption('ca', 'Catalan'),
    SIMPLIFIED_CHINESE_OPTION,
    TRADITIONAL_CHINESE_OPTION,
    languageOption('hr', 'Croatian'),
    languageOption('cs', 'Czech'),
    languageOption('da', 'Danish'),
    languageOption('nl', 'Dutch'),
    languageOption('en-GB', 'English (United Kingdom)'),
    FALLBACK_OPTION,
    languageOption('fi', 'Finnish'),
    languageOption('fr', 'French'),
    languageOption('de', 'German'),
    languageOption('he', 'Hebrew'),
    languageOption('hu', 'Hungarian'),
    languageOption('id', 'Indonesian'),
    languageOption('it', 'Italian'),
    languageOption('ja', 'Japanese'),
    languageOption('ko', 'Korean'),
    languageOption('no', 'Norwegian'),
    languageOption('nb-NO', 'Norwegian Bokmål (Norway)'),
    languageOption('pl', 'Polish'),
    languageOption('pt', 'Portuguese'),
    languageOption('ro', 'Romanian'),
    languageOption('ru', 'Russian'),
    languageOption('sr', 'Serbian'),
    languageOption('sl', 'Slovenian'),
    languageOption('es-CO', 'Spanish (Colombia)'),
    languageOption('es', 'Spanish (Spain)'),
    languageOption('sv', 'Swedish'),
    languageOption('tr', 'Turkish'),
    languageOption('uk', 'Ukrainian'),
    languageOption('cy', 'Welsh'),
];
const OPTIONS_BY_CODE = new Map(LANGUAGE_OPTIONS.map((option) => [option.code.toLowerCase(), option]));
// These shipped region files duplicate their base language. Resolve them to
// the visible row while retaining variants with distinct translations.
const LOCALE_ALIASES = {
    'ar-sa': 'ar',
    'bg-bg': 'bg',
    'ca-es': 'ca',
    'cs-cz': 'cs',
    'cy-gb': 'cy',
    'da-dk': 'da',
    'de-de': 'de',
    'es-es': 'es',
    'fi-fi': 'fi',
    'fr-fr': 'fr',
    'he-il': 'he',
    'hr-hr': 'hr',
    'hu-hu': 'hu',
    'id-id': 'id',
    'it-it': 'it',
    'ko-kr': 'ko',
    'nl-nl': 'nl',
    'pl-pl': 'pl',
    'pt-br': 'pt',
    'ro-ro': 'ro',
    'ru-ru': 'ru',
    'sl-si': 'sl',
    'sr-rs': 'sr',
    'sv-se': 'sv',
    'tr-tr': 'tr',
    'uk-ua': 'uk',
    'en-us': 'en',
    nb: 'nb-no',
};
export const getNativeLanguageLabel = (option) => option.region ? `${option.label} (${option.region})` : option.label;
export const getTriggerLabel = (option) => {
    var _a;
    if (!option.region)
        return option.label;
    const region = (_a = option.code.split('-')[1]) !== null && _a !== void 0 ? _a : option.region;
    return `${option.label} (${region})`;
};
export const resolveLanguageOption = (code) => {
    var _a, _b;
    const normalized = (code !== null && code !== void 0 ? code : '').trim().replace(/_/g, '-').toLowerCase();
    const exact = OPTIONS_BY_CODE.get(normalized);
    if (exact)
        return exact;
    const alias = OPTIONS_BY_CODE.get(LOCALE_ALIASES[normalized]);
    if (alias)
        return alias;
    const [base, second] = normalized.split('-');
    if (base === 'zh') {
        const script = second && /^[a-z]{4}$/.test(second) ? second : undefined;
        // An explicit script takes precedence over the region, including conflicts.
        if (script === 'hant')
            return TRADITIONAL_CHINESE_OPTION;
        if (script === 'hans')
            return SIMPLIFIED_CHINESE_OPTION;
        if (script)
            return FALLBACK_OPTION;
        return second === 'tw' || second === 'hk' || second === 'mo'
            ? TRADITIONAL_CHINESE_OPTION
            : SIMPLIFIED_CHINESE_OPTION;
    }
    return ((_b = (_a = OPTIONS_BY_CODE.get(base)) !== null && _a !== void 0 ? _a : OPTIONS_BY_CODE.get(LOCALE_ALIASES[base])) !== null && _b !== void 0 ? _b : FALLBACK_OPTION);
};
